import type { useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { RitualConfig } from '../../helpers/ritual';
import { CommonIcon } from '../common/CommonIcon';

interface RitualCardNormalStateProps {
    config: RitualConfig;
    completedCount: number;
    totalCount: number;
    mainPressHandlers: ReturnType<typeof useLiquidGlassPress>;
    actionPressHandlers?: ReturnType<typeof useLiquidGlassPress>;
}

export const RitualCardNormalState: FC<RitualCardNormalStateProps> = ({
    config,
    completedCount,
    totalCount,
    mainPressHandlers,
    actionPressHandlers,
}) => {
    const { t } = useTranslation();
    const isSplit = !!actionPressHandlers;

    return (
        <Box
            component={isSplit ? 'div' : 'button'}
            {...(!isSplit && {
                type: 'button',
                'aria-label': config.title,
                onClick: mainPressHandlers.onClick,
                onPointerDown: mainPressHandlers.onPointerDown,
                onPointerMove: mainPressHandlers.onPointerMove,
                onPointerUp: mainPressHandlers.onPointerUp,
                onPointerCancel: mainPressHandlers.onPointerCancel,
            })}
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                width: '100%',
                m: 0,
                p: 0,
                border: isSplit ? 'none' : 0,
                background: isSplit ? 'transparent' : 'none',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
                cursor: isSplit ? 'default' : 'pointer',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
            }}
        >
            <Box
                component={isSplit ? 'button' : 'div'}
                {...(isSplit && {
                    type: 'button',
                    'aria-label': t('ritual.edit', { name: config.title }),
                    onClick: mainPressHandlers.onClick,
                    onPointerDown: mainPressHandlers.onPointerDown,
                    onPointerMove: mainPressHandlers.onPointerMove,
                    onPointerUp: mainPressHandlers.onPointerUp,
                    onPointerCancel: mainPressHandlers.onPointerCancel,
                })}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: '11px 14px',
                    m: 0,
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'inherit',
                    cursor: isSplit ? 'pointer' : 'inherit',
                    WebkitTapHighlightColor: 'transparent',
                    outline: 'none',
                }}
            >
                <Stack direction="column" gap={0.25} minWidth={0}>
                    <Text
                        style={{
                            fontSize: '15px',
                            fontWeight: 500,
                            lineHeight: 1.3,
                            color: 'var(--tg-theme-text-color)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {config.title}
                    </Text>
                    <Text
                        style={{
                            fontSize: '13px',
                            color: 'var(--tgui--hint_color)',
                        }}
                    >
                        {t('ritual.progress', { completed: completedCount, total: totalCount })}
                    </Text>
                </Stack>
            </Box>

            <Box
                sx={{
                    width: '1px',
                    alignSelf: 'stretch',
                    backgroundColor: 'color-mix(in srgb, var(--tg-theme-text-color) 16%, transparent)',
                }}
            />

            <Box
                component={isSplit ? 'button' : 'div'}
                {...(isSplit && {
                    type: 'button',
                    'aria-label': t('ritual.open', { name: config.title }),
                    onClick: actionPressHandlers?.onClick,
                    onPointerDown: actionPressHandlers?.onPointerDown,
                    onPointerMove: actionPressHandlers?.onPointerMove,
                    onPointerUp: actionPressHandlers?.onPointerUp,
                    onPointerCancel: actionPressHandlers?.onPointerCancel,
                })}
                sx={{
                    width: 56,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    m: 0,
                    border: 'none',
                    backgroundColor: actionPressHandlers?.pressed
                        ? 'color-mix(in srgb, var(--tg-theme-text-color) 8%, transparent)'
                        : 'transparent',
                    cursor: isSplit ? 'pointer' : 'inherit',
                    WebkitTapHighlightColor: 'transparent',
                    outline: 'none',
                    transition: 'background-color 0.12s ease',
                    '&:active': {
                        backgroundColor: 'color-mix(in srgb, var(--tg-theme-text-color) 8%, transparent)',
                    },
                }}
            >
                <CommonIcon
                    icon={config.icon}
                    type="filled"
                    size={26}
                    color="var(--tgui--button_color)"
                />
            </Box>
        </Box>
    );
};
